import SetPasswordForm from './components/SetPasswordForm';
import SetDiscountForm from './components/DiscountForm';
import MassCopyRatesForm from './components/MassCopyRatesForm';
import CopyRatesForm from './components/CopyRatesForm';
import UserForm from './components/UserForm';
import ActivityLog from './components/ActivityLog';
import SignInTable from './components/SignInTable';
import OrderRefundsHistory from './components/OrderRrefundsHistory';

import { onMassChangeStatus, onMassResetRates } from '../../redux/actions/users/actions';

/**
 * @function
 * @name getObjectByNameFromArray
 * @description Поиск искомого значения в массиве
 * @param arr {Array} - массив для поиска
 * @param name {String} - значения для поиска
 */
const getObjectByNameFromArray = (arr, name) => arr?.find((item) => item?.name === name);

/**
 * @function
 * @name massAvailable
 * @description Проверка доступности массовых действий
 * @param config {Object} - глобальный конфиг
 */
const massAvailable = (config) => config.access?.mass_change_status || config.access?.mass_copy_rates || config.access?.mass_reset_rates;

/**
 * @function
 * @name getCustomUserFieldsAccess
 * @description Проверка добавленных значений юзера
 * @param fields {Object} - поля с доступами
 */
const getCustomUserFieldsAccess = (fields) => fields.includes('first_name')
  || fields.includes('last_name')
  || fields.includes('telegram')
  || fields.includes('skype')
  || fields.includes('phone')
  || fields.includes('whatsapp')
  || fields.includes('website');

/**
 * @function
 * @name generateTableHead
 * @description Формирование заголовков таблицы
 * @param fields {Object} - поля с доступами
 * @param columns {Array} - доступные колонки
 * @param config {Object} - глобальный конфиг
 */
export const generateTableHead = (fields = [], columns, config) => {
  const tableHead = [
    massAvailable(config) && { title: 'All' },
    { title: 'ID', ...getObjectByNameFromArray(columns, 'id') },
    { title: 'Username' },
    { title: 'Email' },
    getCustomUserFieldsAccess(fields) && { title: '' },
    { title: 'Balance', ...getObjectByNameFromArray(columns, 'balance') },
    { title: 'Spent', ...getObjectByNameFromArray(columns, 'spent') },
    { title: 'Status' },
    { title: 'Created', ...getObjectByNameFromArray(columns, 'date') },
    { title: 'Last auth', ...getObjectByNameFromArray(columns, 'lastlogin') },
    { title: 'Discount', ...getObjectByNameFromArray(columns, 'discount') },
    { title: 'Rates', ...getObjectByNameFromArray(columns, 'custom_rates') },
    { title: 'Actions' },
  ];

  return fields && tableHead.filter(Boolean);
};

/**
 * @function
 * @name makeTableRow
 * @description Формирование тела таблицы
 * @param fields {Object} - поля с доступами
 * @param item {Object} - элемент таблицы
 * @param config {Object} - глобальный конфиг
 */
export const makeTableRow = (item, fields = [], config = {}) => {
  const tableRow = [
    massAvailable(config) && { value: '', type: 'checkbox' },
    { value: item.id, type: 'id' },
    { value: item.username, type: 'username' },
    { value: item.email, type: 'email' },
    getCustomUserFieldsAccess(fields) && {
      value: item.user_details,
      type: 'fields',
    },
    { value: item.balance, type: 'balance' },
    { value: item.spent, type: 'spent' },
    { value: item.status_title, type: 'status' },
    { value: item.created, type: 'created' },
    { value: item.last_login || '', type: 'last' },
    config?.access?.update_discount && { value: '', type: 'discount' },
    config?.access?.users_custom_rates && { value: '', type: 'rates' },
    { value: '', type: 'actions' },
  ];

  return fields && tableRow.filter(Boolean);
};

export const getUserAccess = (element, config = {}) => {
  const { access } = element;
  const userAccess = [];

  for (const obj in access) {
    if (access[obj] === true) {
      userAccess.push(obj);
    }
  }

  if (config?.sign_in_history) {
    access.sign_in_history = true;
    access.users_with_same_ips = true;
  }

  const defaultAccess = [
    {
      title: 'Edit user',
      id: 0,
      defActionName: 'update_user',
    },
    {
      title: 'Set password',
      id: 1,
      defActionName: 'set_password',
    },
    {
      title: 'Copy rates',
      id: 2,
      defActionName: 'copy_custom_rates',
    },
    {
      title: 'Order refunds',
      id: 8,
      defActionName: 'refunds',
    },
    {
      title: 'Sign-in history',
      id: 6,
      defActionName: 'sign_in_history',
    },
    {
      title: 'Show users with same IPs',
      id: 7,
      defActionName: 'users_with_same_ips',
    },
    {
      title: element.is_inactive === true ? 'Activate user' : 'Suspend user',
      id: 3,
      defActionName: 'change_status',
    },
    {
      title: 'Sign in as user',
      id: 4,
      defActionName: 'auth',
    },
    {
      title: 'Activity log',
      id: 5,
      defActionName: 'activity_log',
    },
  ];

  const formatAccessMassActions = defaultAccess?.filter((item) => Object.keys(access)
    .filter((item) => access[item])
    .includes(item.defActionName));

  return formatAccessMassActions;
};

export const openAddUser = (setOpen, setSettings) => {
  setSettings((prev) => ({
    ...prev,
    mode: 0,
    title: 'Add user',
    okBtnText: 'Add user',
    cancelBtnText: 'Cancel',
  }));

  setOpen(true);
};

export const openEditUser = (setOpen, setSetting, id) => {
  setSetting((prev) => ({
    ...prev,
    mode: 1,
    title: `Edit user (ID: ${id})`,
    okBtnText: 'Save changes',
    cancelBtnText: 'Cancel',
    userId: id,
  }));

  setOpen(true);
};

export const generateActiveClass = (values, item) => (values.includes(item.id) && item.is_inactive === true
  ? 'active grey'
  : values.includes(item.id)
    ? 'active'
    : item.is_inactive === true
      ? 'grey'
      : '');

export const getMassAccess = (access) => {
  const massAccessArr = [];
  const items = [];

  for (const obj in access) {
    if (obj.indexOf('mass') !== -1 && access[obj] === true) {
      massAccessArr.push(obj);
    }
  }
  massAccessArr.forEach((el) => {
    if (el.includes('mass_change_status')) {
      items.push({ title: 'Suspend all', id: items.length + 1 });
      items.push({ title: 'Activate all', id: items.length + 1 });
    } else if (el.includes('reset')) {
      items.push({ title: 'Delete custom rates', id: items.length + 1 });
    } else if (el.includes('copy')) {
      items.push({ title: 'Copy rates from user', id: items.length + 1 });
    }
  });

  return items;
};

export const toggleRates = (setUsername, setModalText, setRatesOpen, item) => {
  if (item) {
    setUsername(`${item.username} (${item.custom_rates})`);
    setModalText({
      title: `Copy custom rates (ID: ${item.id})`,
      okBtnText: 'Copy rates',
      cancelBtnText: 'Cancel',
    });
  }
  setRatesOpen((prev) => !prev);
};

export const submitMassAction = (props) => {
  const { title, values, setUsers } = props;

  switch (title.trim()) {
  case 'Suspend all':
    setUsers([]);
    return onMassChangeStatus(1, values);

  case 'Activate all':
    setUsers([]);
    return onMassChangeStatus(0, values);

  case 'Delete custom rates':
    setUsers([]);
    return onMassResetRates(values);

  default:
    break;
  }
};

export const getModal = (index, user = { id: -1 }) => {
  const modals = [
    {
      modalText: {
        title: `Set password (ID: ${user.id})`,
        okBtnText: 'Set password',
        cancelBtnText: 'Cancel',
      },
      Form: SetPasswordForm,
      user,
    },
    {
      modalText: {
        title: 'Add user',
        okBtnText: 'Add user',
        cancelBtnText: 'Cancel',
      },
      Form: UserForm,
      mode: 0,
      user,
    },
    {
      modalText: {
        title: `Edit user (ID: ${user.id})`,
        okBtnText: 'Save changes',
        cancelBtnText: 'Cancel',
      },
      Form: UserForm,
      mode: 1,
      user,
      forEditUser: true,
    },
    {
      modalText: {
        title: `Copy custom rates (ID: ${user.id})`,
        okBtnText: 'Copy rates',
        cancelBtnText: 'Cancel',
      },
      user,
      Form: CopyRatesForm,
    },
    {
      modalText: {
        title: 'Copy custom rates',
        okBtnText: 'Copy rates',
        cancelBtnText: 'Cancel',
      },
      Form: MassCopyRatesForm,
    },
    {
      modalText: {
        title: `Activity log (ID: ${user.id})`,
        okBtnText: '',
        cancelBtnText: 'Ok',
      },
      Form: ActivityLog,
      user,
      forAddRates: true,
      hideSubmitBtn: true,
    },
    {
      modalText: {
        title: 'User sign-in history',
        okBtnText: '',
        cancelBtnText: '',
      },
      Form: SignInTable,
      user,
      forAddRates: true,
      hideSubmitBtn: true,
      hideFooter: true,
    },
    {
      modalText: {
        title: `Set discount (ID: ${user.id})`,
        okBtnText: 'Save changes',
        cancelBtnText: 'Cancel',
      },
      Form: SetDiscountForm,
      user,
    },
    {
      modalText: {
        title: 'Order refunds history',
        okBtnText: '',
        cancelBtnText: '',
      },
      size: 'lg',
      className: 'orders-refunds__modal',
      Form: OrderRefundsHistory,
      user,
      hideSubmitBtn: true,
      hideFooter: true,
    },
  ];

  return modals[index];
};

export const detectDataValue = (userRates, ratesValues, wasAddRates) => {
  if (!wasAddRates) {
    return true;
  }
  if (userRates?.length !== 0 || ratesValues?.length !== 0) {
    return false;
  }
  return false;
};

/**
 * @name prepareConfirmBtnsText
 * @param title {String} - Заголовок события
 * @param actionType {Number} - Тип события
 */
export const prepareConfirmBtnsText = (title, actionType) => {
  if (actionType === 'single') {
    switch (title) {
    case 'Suspend user':
      return {
        confirmBtnText: 'Suspend',
        cancelBtnText: 'Cancel',
      };
    case 'Activate user':
      return {
        confirmBtnText: 'Activate',
        cancelBtnText: 'Cancel',
        submitStyle: 'primary',
      };

    default:
      return {
        confirmBtnText: '',
        cancelBtnText: '',
      };
    }
  }

  if (actionType === 'mass') {
    switch (title) {
    case 'Suspend all':
      return {
        confirmBtnText: 'Suspend',
        cancelBtnText: 'Cancel',
      };
    case 'Activate all':
      return {
        confirmBtnText: 'Activate',
        cancelBtnText: 'Cancel',
        submitStyle: 'primary',
      };
    case 'Delete custom rates':
      return {
        confirmBtnText: 'Delete',
        cancelBtnText: 'Cancel',
      };

    default:
      return {
        confirmBtnText: '',
        cancelBtnText: '',
      };
    }
  }
};
